(function (global) {
  'use strict';

  function createDefaultFeedingEngineConfig() {
    return {
      schemaVersion: '1.0.0',
      status: 'pending-approval',
      guides: [],
      productMappings: [],
      fallbackMessaging: {
        contactTeamMessage: 'Please contact Pet Food Australia for personalised guidance.'
      },
      metadata: {
        owner: 'Nutrition Team',
        status: 'Pending approval',
        lastUpdated: '2026-07-13'
      }
    };
  }

  function isObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function isPendingGuide(guide) {
    return !!(guide && guide.metadata && typeof guide.metadata.status === 'string' && /pending/i.test(guide.metadata.status));
  }

  function isPositiveNumber(value) {
    return typeof value === 'number' && value > 0;
  }

  function getFeedingEngine(config) {
    if (!config) {
      return null;
    }

    if (config.feedingEngine) {
      return config.feedingEngine;
    }

    if (config.schemaVersion && Array.isArray(config.guides)) {
      return config;
    }

    return null;
  }

  function getContactTeamMessage(feedingEngine) {
    return feedingEngine && feedingEngine.fallbackMessaging && feedingEngine.fallbackMessaging.contactTeamMessage
      ? feedingEngine.fallbackMessaging.contactTeamMessage
      : 'Please contact Pet Food Australia for personalised guidance.';
  }

  function createFallbackPlan(input, feedingEngine, reason) {
    const contactTeamMessage = getContactTeamMessage(feedingEngine);
    const fallbackNotes = reason ? [reason, contactTeamMessage] : [contactTeamMessage];

    return {
      status: 'contact-team',
      recipeId: input && input.recipeId ? input.recipeId : null,
      lifecycleStage: input && input.lifecycleStage ? input.lifecycleStage : null,
      dailyGrams: null,
      mealsPerDay: null,
      gramsPerMeal: null,
      recommendedBagSize: null,
      estimatedDaysPerBag: null,
      suggestedReorderDay: null,
      transitionSchedule: [],
      calculationNotes: fallbackNotes,
      appliedAdjustments: [],
      confidence: 'pending-approval',
      contactTeamFallback: true,
      feedingGuideId: null,
      weightBandId: null,
      expectedAdultWeightBandId: null,
      ageBandId: null,
      calculationMethod: 'fallback',
      expectedAdultWeightKg: input && typeof input.expectedAdultWeightKg === 'number' ? input.expectedAdultWeightKg : null,
      puppyAgeMonths: input && typeof input.puppyAgeMonths === 'number' ? input.puppyAgeMonths : null,
      inputWeightKg: input && typeof input.weightKg === 'number' ? input.weightKg : null,
      currentBodyWeightKg: input && typeof input.currentBodyWeightKg === 'number' ? input.currentBodyWeightKg : null,
      gramsPerDay: null,
      minimumGramsPerDay: null,
      maximumGramsPerDay: null,
      gramsPerKgBodyWeight: null,
      estimated30DayConsumptionGrams: null,
      fallbackReason: reason || 'unsupported-calculation'
    };
  }

  function sortByRangeStart(entries, key) {
    return entries.slice().sort(function (left, right) {
      return left[key] - right[key];
    });
  }

  function validateBoundarySequence(entries, minimumKey, maximumKey, lowerKey, upperKey, overlapError, gapError, errors) {
    const sortedEntries = sortByRangeStart(entries, minimumKey);

    for (let index = 1; index < sortedEntries.length; index += 1) {
      const previousEntry = sortedEntries[index - 1];
      const currentEntry = sortedEntries[index];

      if (currentEntry[minimumKey] < previousEntry[maximumKey]) {
        errors.push(overlapError);
        return;
      }

      if (currentEntry[minimumKey] === previousEntry[maximumKey]) {
        if (previousEntry[upperKey] && currentEntry[lowerKey]) {
          errors.push(overlapError);
          return;
        }

        if (!previousEntry[upperKey] && !currentEntry[lowerKey]) {
          errors.push(gapError);
          return;
        }
      }
    }
  }

  function validatePointGuide(guide, errors) {
    const points = Array.isArray(guide.points) ? guide.points : [];

    if (!points.length && !isPendingGuide(guide)) {
      errors.push('feedingEngine.guides point-based guides must include at least one valid point.');
      return;
    }

    if (!points.length) {
      return;
    }

    const seenWeights = new Set();
    let previousWeight = null;

    points.forEach(function (point) {
      if (!isObject(point)) {
        errors.push('feedingEngine.guides point definitions must be objects.');
        return;
      }

      if (!isPositiveNumber(point.weightKg)) {
        errors.push('feedingEngine.guides point weights must be positive numbers.');
      }

      if (!isPositiveNumber(point.gramsPerDay)) {
        errors.push('feedingEngine.guides point gramsPerDay values must be positive numbers.');
      }

      if (typeof point.weightKg === 'number' && seenWeights.has(point.weightKg)) {
        errors.push('feedingEngine.guides point weights must be unique.');
      }

      if (typeof point.weightKg === 'number') {
        seenWeights.add(point.weightKg);
      }

      if (typeof point.weightKg === 'number' && previousWeight !== null && point.weightKg <= previousWeight) {
        errors.push('feedingEngine.guides point weights must be strictly increasing.');
      }

      if (typeof point.weightKg === 'number') {
        previousWeight = point.weightKg;
      }
    });

    if (guide.interpolationMode && guide.interpolationMode !== 'linear') {
      errors.push('feedingEngine.guides interpolationMode must be linear for point-based guides.');
    }
  }

  function validateBandGuide(guide, errors) {
    const bands = Array.isArray(guide.bands) ? guide.bands : [];

    if (!bands.length && !isPendingGuide(guide)) {
      errors.push('feedingEngine.guides band-based guides must include at least one valid band.');
      return;
    }

    if (!bands.length) {
      return;
    }

    const sortedBands = bands.slice().sort(function (left, right) {
      const leftStart = typeof left.minWeightKg === 'number' ? left.minWeightKg : Number.NEGATIVE_INFINITY;
      const rightStart = typeof right.minWeightKg === 'number' ? right.minWeightKg : Number.NEGATIVE_INFINITY;
      return leftStart - rightStart;
    });

    sortedBands.forEach(function (band) {
      if (!isObject(band)) {
        errors.push('feedingEngine.guides bands must be objects.');
        return;
      }

      if (typeof band.minWeightKg !== 'number' && band.minWeightKg !== null) {
        errors.push('feedingEngine.guides band minimum weights must be numbers or null.');
      }

      if (typeof band.maxWeightKg !== 'number' && band.maxWeightKg !== null) {
        errors.push('feedingEngine.guides band maximum weights must be numbers or null.');
      }

      if (band.isFinite === true) {
        if (typeof band.minWeightKg !== 'number' || typeof band.maxWeightKg !== 'number') {
          errors.push('feedingEngine.guides finite bands must include numeric minimum and maximum weights.');
        } else if (band.minWeightKg >= band.maxWeightKg) {
          errors.push('feedingEngine.guides finite bands must have a positive range.');
        }
      }

      if (!isPositiveNumber(band.minGramsPerDay)) {
        errors.push('feedingEngine.guides band minimum gramsPerDay values must be positive numbers.');
      }

      if (!isPositiveNumber(band.maxGramsPerDay)) {
        errors.push('feedingEngine.guides band maximum gramsPerDay values must be positive numbers.');
      }

      if (isPositiveNumber(band.minGramsPerDay) && isPositiveNumber(band.maxGramsPerDay) && band.minGramsPerDay > band.maxGramsPerDay) {
        errors.push('feedingEngine.guides band values must not decrease from minimum to maximum.');
      }

      if (band.isOpenEndedMin === true && typeof band.minWeightKg === 'number') {
        errors.push('feedingEngine.guides open-ended minimum bands must not include a numeric minimum weight.');
      }

      if (band.isOpenEndedMax === true && typeof band.maxWeightKg === 'number') {
        errors.push('feedingEngine.guides open-ended maximum bands must not include a numeric maximum weight.');
      }

      if (band.isOpenEndedMin === true && band.isOpenEndedMax === true) {
        errors.push('feedingEngine.guides bands cannot be open-ended at both ends.');
      }
    });

    if (guide.lifecycleStage === 'adult') {
      for (let index = 1; index < sortedBands.length; index += 1) {
        const previousBand = sortedBands[index - 1];
        const currentBand = sortedBands[index];

        if (previousBand.isFinite === true && currentBand.isFinite === true && previousBand.maxWeightKg !== currentBand.minWeightKg) {
          errors.push('feedingEngine.guides finite bands must be continuous.');
        }
      }
    }

    if (guide.interpolationMode && guide.interpolationMode !== 'linear') {
      errors.push('feedingEngine.guides interpolationMode must be linear for band-based guides.');
    }
  }

  function validateAgeBasedRangeGuide(guide, errors) {
    const expectedAdultWeightBands = Array.isArray(guide.expectedAdultWeightBands) ? guide.expectedAdultWeightBands : [];

    if (guide.interpolationMode) {
      errors.push('feedingEngine.guides age-based range guides must not configure interpolation.');
    }

    if (!expectedAdultWeightBands.length && !isPendingGuide(guide)) {
      errors.push('feedingEngine.guides age-based range guides must include expectedAdultWeightBands.');
      return;
    }

    if (!expectedAdultWeightBands.length) {
      return;
    }

    expectedAdultWeightBands.forEach(function (weightBand) {
      if (!isObject(weightBand)) {
        errors.push('feedingEngine.guides expectedAdultWeightBands must be objects.');
        return;
      }

      if (!isPositiveNumber(weightBand.minimumAdultWeightKg) || !isPositiveNumber(weightBand.maximumAdultWeightKg)) {
        errors.push('feedingEngine.guides expectedAdultWeightBands must include positive numeric boundaries.');
      } else if (weightBand.minimumAdultWeightKg >= weightBand.maximumAdultWeightKg) {
        errors.push('feedingEngine.guides expectedAdultWeightBands must have a positive range.');
      }

      if (typeof weightBand.inclusiveLower !== 'boolean' || typeof weightBand.inclusiveUpper !== 'boolean') {
        errors.push('feedingEngine.guides expectedAdultWeightBands must declare inclusiveLower and inclusiveUpper.');
      }

      if (!Array.isArray(weightBand.ageBands) || !weightBand.ageBands.length) {
        errors.push('feedingEngine.guides expectedAdultWeightBands must include ageBands.');
        return;
      }

      weightBand.ageBands.forEach(function (ageBand) {
        if (!isObject(ageBand)) {
          errors.push('feedingEngine.guides ageBands must be objects.');
          return;
        }

        if (!isPositiveNumber(ageBand.minimumAgeMonths) || !isPositiveNumber(ageBand.maximumAgeMonths)) {
          errors.push('feedingEngine.guides ageBands must include positive numeric boundaries.');
        } else if (ageBand.minimumAgeMonths >= ageBand.maximumAgeMonths) {
          errors.push('feedingEngine.guides ageBands must have a positive range.');
        }

        if (typeof ageBand.inclusiveLower !== 'boolean' || typeof ageBand.inclusiveUpper !== 'boolean') {
          errors.push('feedingEngine.guides ageBands must declare inclusiveLower and inclusiveUpper.');
        }

        if (!isPositiveNumber(ageBand.minimumGramsPerDay) || !isPositiveNumber(ageBand.maximumGramsPerDay)) {
          errors.push('feedingEngine.guides ageBands must include positive minimumGramsPerDay and maximumGramsPerDay values.');
        } else if (ageBand.minimumGramsPerDay > ageBand.maximumGramsPerDay) {
          errors.push('feedingEngine.guides ageBands minimumGramsPerDay must not exceed maximumGramsPerDay.');
        }
      });

      validateBoundarySequence(
        weightBand.ageBands,
        'minimumAgeMonths',
        'maximumAgeMonths',
        'inclusiveLower',
        'inclusiveUpper',
        'feedingEngine.guides ageBands must not overlap.',
        'feedingEngine.guides ageBands must not leave gaps at shared boundaries.',
        errors
      );
    });

    validateBoundarySequence(
      expectedAdultWeightBands,
      'minimumAdultWeightKg',
      'maximumAdultWeightKg',
      'inclusiveLower',
      'inclusiveUpper',
      'feedingEngine.guides expectedAdultWeightBands must not overlap.',
      'feedingEngine.guides expectedAdultWeightBands must not leave gaps at shared boundaries.',
      errors
    );
  }

  function validateFeedingEngineConfig(config) {
    const errors = [];
    const feedingEngine = getFeedingEngine(config);

    if (!feedingEngine) {
      return { isValid: false, errors: ['feedingEngine configuration is missing.'] };
    }

    if (feedingEngine.schemaVersion !== '1.0.0') {
      errors.push('feedingEngine.schemaVersion must be 1.0.0.');
    }

    if (!feedingEngine.status || typeof feedingEngine.status !== 'string') {
      errors.push('feedingEngine.status is required.');
    }

    if (!Array.isArray(feedingEngine.guides)) {
      errors.push('feedingEngine.guides must be an array.');
    } else {
      const guideIds = new Set();

      feedingEngine.guides.forEach(function (guide) {
        if (!isObject(guide)) {
          errors.push('feedingEngine.guides entries must be objects.');
          return;
        }

        if (!guide.id || typeof guide.id !== 'string') {
          errors.push('feedingEngine.guides entries must include a string id.');
        } else if (guideIds.has(guide.id)) {
          errors.push('feedingEngine.guides must not contain duplicate ids.');
        } else {
          guideIds.add(guide.id);
        }

        if (!guide.lifecycleStage || typeof guide.lifecycleStage !== 'string') {
          errors.push('feedingEngine.guides entries must include a lifecycleStage.');
        }

        if (!guide.guideType || !['point', 'band', 'age-based-range', 'puppy-age-based-range'].includes(guide.guideType)) {
          errors.push('feedingEngine.guides entries must include a supported guideType.');
        }

        if (!guide.unit || typeof guide.unit !== 'string') {
          errors.push('feedingEngine.guides entries must include a unit.');
        }

        if (guide.lifecycleStage === 'puppy' && guide.guideType !== 'age-based-range' && guide.guideType !== 'puppy-age-based-range' && !guide.sizeClass) {
          errors.push('feedingEngine.guides puppy legacy guides must include a sizeClass.');
        }

        if (guide.guideType === 'point') {
          validatePointGuide(guide, errors);
        } else if (guide.guideType === 'band') {
          validateBandGuide(guide, errors);
        } else if (guide.guideType === 'age-based-range' || guide.guideType === 'puppy-age-based-range') {
          validateAgeBasedRangeGuide(guide, errors);
        }
      });
    }

    if (!Array.isArray(feedingEngine.productMappings)) {
      errors.push('feedingEngine.productMappings must be an array.');
    } else {
      const mappingIds = new Set();
      const guideIds = new Set((feedingEngine.guides || []).map(function (guide) {
        return guide.id;
      }));

      feedingEngine.productMappings.forEach(function (mapping) {
        if (!isObject(mapping)) {
          errors.push('feedingEngine.productMappings entries must be objects.');
          return;
        }

        if (!mapping.id || typeof mapping.id !== 'string') {
          errors.push('feedingEngine.productMappings entries must include a string id.');
        } else if (mappingIds.has(mapping.id)) {
          errors.push('feedingEngine.productMappings must not contain duplicate ids.');
        } else {
          mappingIds.add(mapping.id);
        }

        if (!mapping.productId || typeof mapping.productId !== 'string') {
          errors.push('feedingEngine.productMappings entries must include a productId.');
        }

        if (mapping.guideId && !guideIds.has(mapping.guideId)) {
          errors.push('feedingEngine.productMappings entries must reference a known guideId.');
        }
      });
    }

    if (!feedingEngine.fallbackMessaging || !isObject(feedingEngine.fallbackMessaging)) {
      errors.push('feedingEngine.fallbackMessaging must be an object.');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  function createPlaceholderFeedingPlan(input) {
    return {
      status: 'pending-approval',
      recipeId: input && input.recipeId ? input.recipeId : null,
      lifecycleStage: input && input.lifecycleStage ? input.lifecycleStage : null,
      dailyGrams: null,
      mealsPerDay: null,
      gramsPerMeal: null,
      recommendedBagSize: null,
      estimatedDaysPerBag: null,
      suggestedReorderDay: null,
      transitionSchedule: [],
      calculationNotes: [
        'Feeding quantities are pending Pet Food Australia approval.',
        'No calculated feeding results are available yet.'
      ],
      appliedAdjustments: [],
      confidence: 'pending-approval',
      contactTeamFallback: true,
      feedingGuideId: null,
      weightBandId: null,
      expectedAdultWeightBandId: null,
      ageBandId: null,
      calculationMethod: 'pending',
      expectedAdultWeightKg: input && typeof input.expectedAdultWeightKg === 'number' ? input.expectedAdultWeightKg : null,
      puppyAgeMonths: input && typeof input.puppyAgeMonths === 'number' ? input.puppyAgeMonths : null,
      inputWeightKg: input && typeof input.weightKg === 'number' ? input.weightKg : null,
      currentBodyWeightKg: input && typeof input.currentBodyWeightKg === 'number' ? input.currentBodyWeightKg : null,
      gramsPerDay: null,
      minimumGramsPerDay: null,
      maximumGramsPerDay: null,
      gramsPerKgBodyWeight: null,
      estimated30DayConsumptionGrams: null,
      fallbackReason: null
    };
  }

  function findGuideById(guides, guideId) {
    if (!Array.isArray(guides) || !guideId) {
      return null;
    }

    return guides.find(function (guide) {
      return guide && guide.id === guideId;
    }) || null;
  }

  function findGuideBySizeClass(guides, lifecycleStage, sizeClass) {
    if (!Array.isArray(guides) || !sizeClass) {
      return null;
    }

    return guides.find(function (guide) {
      return guide && guide.lifecycleStage === lifecycleStage && guide.sizeClass === sizeClass;
    }) || null;
  }

  function findProductMapping(productMappings, recipeId, lifecycleStage) {
    if (!Array.isArray(productMappings) || !recipeId) {
      return null;
    }

    return productMappings.find(function (mapping) {
      return mapping && mapping.productId === recipeId && (!mapping.lifecycleStage || mapping.lifecycleStage === lifecycleStage);
    }) || null;
  }

  function resolvePointGuide(guide, weightKg) {
    const points = Array.isArray(guide.points)
      ? guide.points.slice().sort(function (left, right) { return left.weightKg - right.weightKg; })
      : [];

    if (!points.length) {
      return null;
    }

    const exactPoint = points.find(function (point) {
      return point.weightKg === weightKg;
    });

    if (exactPoint) {
      return {
        gramsPerDay: exactPoint.gramsPerDay,
        calculationMethod: 'exact-point',
        feedingGuideId: guide.id,
        weightBandId: null,
        fallbackReason: null
      };
    }

    if (weightKg < points[0].weightKg || weightKg > points[points.length - 1].weightKg) {
      return null;
    }

    for (let index = 0; index < points.length - 1; index += 1) {
      const lowerPoint = points[index];
      const upperPoint = points[index + 1];

      if (weightKg > lowerPoint.weightKg && weightKg < upperPoint.weightKg) {
        const ratio = (weightKg - lowerPoint.weightKg) / (upperPoint.weightKg - lowerPoint.weightKg);
        return {
          gramsPerDay: lowerPoint.gramsPerDay + (upperPoint.gramsPerDay - lowerPoint.gramsPerDay) * ratio,
          calculationMethod: 'interpolated-point',
          feedingGuideId: guide.id,
          weightBandId: null,
          fallbackReason: null
        };
      }
    }

    return null;
  }

  function resolveBandGuide(guide, weightKg) {
    const bands = Array.isArray(guide.bands) ? guide.bands.slice() : [];

    if (!bands.length) {
      return null;
    }

    if (guide.lifecycleStage === 'adult' && (weightKg < 5 || weightKg > 50)) {
      return null;
    }

    const sortedBands = bands.slice().sort(function (left, right) {
      const leftStart = typeof left.minWeightKg === 'number' ? left.minWeightKg : Number.NEGATIVE_INFINITY;
      const rightStart = typeof right.minWeightKg === 'number' ? right.minWeightKg : Number.NEGATIVE_INFINITY;
      return leftStart - rightStart;
    });

    let matchingBand = null;

    for (let index = 0; index < sortedBands.length; index += 1) {
      const band = sortedBands[index];
      const startsInside = typeof band.minWeightKg === 'number' ? weightKg >= band.minWeightKg : true;
      const endsInside = band.isOpenEndedMax === true || typeof band.maxWeightKg !== 'number' ? true : weightKg <= band.maxWeightKg;

      if (startsInside && endsInside) {
        matchingBand = band;
        break;
      }
    }

    if (!matchingBand || matchingBand.isFinite !== true || matchingBand.isOpenEndedMin === true || matchingBand.isOpenEndedMax === true) {
      return null;
    }

    if (matchingBand.minGramsPerDay === matchingBand.maxGramsPerDay) {
      return {
        gramsPerDay: matchingBand.minGramsPerDay,
        calculationMethod: 'exact-band',
        feedingGuideId: guide.id,
        weightBandId: matchingBand.id,
        fallbackReason: null
      };
    }

    if (weightKg === matchingBand.minWeightKg) {
      return {
        gramsPerDay: matchingBand.minGramsPerDay,
        calculationMethod: 'exact-band',
        feedingGuideId: guide.id,
        weightBandId: matchingBand.id,
        fallbackReason: null
      };
    }

    if (weightKg === matchingBand.maxWeightKg) {
      return {
        gramsPerDay: matchingBand.maxGramsPerDay,
        calculationMethod: 'exact-band',
        feedingGuideId: guide.id,
        weightBandId: matchingBand.id,
        fallbackReason: null
      };
    }

    if (weightKg > matchingBand.minWeightKg && weightKg < matchingBand.maxWeightKg) {
      const ratio = (weightKg - matchingBand.minWeightKg) / (matchingBand.maxWeightKg - matchingBand.minWeightKg);
      return {
        gramsPerDay: matchingBand.minGramsPerDay + (matchingBand.maxGramsPerDay - matchingBand.minGramsPerDay) * ratio,
        calculationMethod: 'interpolated-band',
        feedingGuideId: guide.id,
        weightBandId: matchingBand.id,
        fallbackReason: null
      };
    }

    return null;
  }

  function matchesConfiguredRange(value, minimum, maximum, inclusiveLower, inclusiveUpper) {
    if (typeof minimum === 'number') {
      if (inclusiveLower ? value < minimum : value <= minimum) {
        return false;
      }
    }

    if (typeof maximum === 'number') {
      if (inclusiveUpper ? value > maximum : value >= maximum) {
        return false;
      }
    }

    return true;
  }

  function resolveConfiguredBand(entries, value, minimumKey, maximumKey) {
    const sortedEntries = sortByRangeStart(entries, minimumKey);

    for (let index = 0; index < sortedEntries.length; index += 1) {
      const entry = sortedEntries[index];
      if (matchesConfiguredRange(value, entry[minimumKey], entry[maximumKey], entry.inclusiveLower, entry.inclusiveUpper)) {
        return entry;
      }
    }

    return null;
  }

  function resolveAgeBasedRangeGuide(guide, expectedAdultWeightKg, puppyAgeMonths) {
    const expectedAdultWeightBands = Array.isArray(guide.expectedAdultWeightBands) ? guide.expectedAdultWeightBands : [];

    if (!expectedAdultWeightBands.length) {
      return null;
    }

    const weightBand = resolveConfiguredBand(
      expectedAdultWeightBands,
      expectedAdultWeightKg,
      'minimumAdultWeightKg',
      'maximumAdultWeightKg'
    );

    if (!weightBand || !Array.isArray(weightBand.ageBands) || !weightBand.ageBands.length) {
      return null;
    }

    const ageBand = resolveConfiguredBand(weightBand.ageBands, puppyAgeMonths, 'minimumAgeMonths', 'maximumAgeMonths');

    if (!ageBand) {
      return null;
    }

    return {
      minimumGramsPerDay: ageBand.minimumGramsPerDay,
      maximumGramsPerDay: ageBand.maximumGramsPerDay,
      expectedAdultWeightBandId: weightBand.bandId || null,
      ageBandId: ageBand.bandId || null,
      feedingGuideId: guide.id,
      calculationMethod: 'approved-range',
      fallbackReason: null
    };
  }

  function buildCalculatedPlan(input, guide, resolvedValue) {
    const weightKg = input && typeof input.weightKg === 'number' ? input.weightKg : null;
    const currentBodyWeightKg = input && typeof input.currentBodyWeightKg === 'number' ? input.currentBodyWeightKg : null;
    const expectedAdultWeightKg = input && typeof input.expectedAdultWeightKg === 'number' ? input.expectedAdultWeightKg : null;
    const puppyAgeMonths = input && typeof input.puppyAgeMonths === 'number' ? input.puppyAgeMonths : null;
    const gramsPerDay = resolvedValue && typeof resolvedValue.gramsPerDay === 'number' ? resolvedValue.gramsPerDay : null;
    const minimumGramsPerDay = resolvedValue && typeof resolvedValue.minimumGramsPerDay === 'number' ? resolvedValue.minimumGramsPerDay : null;
    const maximumGramsPerDay = resolvedValue && typeof resolvedValue.maximumGramsPerDay === 'number' ? resolvedValue.maximumGramsPerDay : null;
    const gramsPerKgBodyWeight = weightKg && gramsPerDay ? gramsPerDay / weightKg : null;
    const estimated30DayConsumptionGrams = gramsPerDay ? gramsPerDay * 30 : null;

    return {
      status: 'calculated',
      recipeId: input && input.recipeId ? input.recipeId : null,
      lifecycleStage: input && input.lifecycleStage ? input.lifecycleStage : null,
      dailyGrams: gramsPerDay,
      mealsPerDay: null,
      gramsPerMeal: null,
      recommendedBagSize: null,
      estimatedDaysPerBag: null,
      suggestedReorderDay: null,
      transitionSchedule: [],
      calculationNotes: [
        'Calculated from the approved feeding guide.',
        'No pricing or shopping logic was applied.'
      ],
      appliedAdjustments: [],
      confidence: 'approved',
      contactTeamFallback: false,
      feedingGuideId: resolvedValue && resolvedValue.feedingGuideId ? resolvedValue.feedingGuideId : (guide ? guide.id : null),
      weightBandId: resolvedValue && resolvedValue.weightBandId ? resolvedValue.weightBandId : null,
      expectedAdultWeightBandId: resolvedValue && resolvedValue.expectedAdultWeightBandId ? resolvedValue.expectedAdultWeightBandId : null,
      ageBandId: resolvedValue && resolvedValue.ageBandId ? resolvedValue.ageBandId : null,
      calculationMethod: resolvedValue && resolvedValue.calculationMethod ? resolvedValue.calculationMethod : 'fallback',
      expectedAdultWeightKg: expectedAdultWeightKg,
      puppyAgeMonths: puppyAgeMonths,
      inputWeightKg: weightKg,
      currentBodyWeightKg: currentBodyWeightKg,
      gramsPerDay: gramsPerDay,
      minimumGramsPerDay: minimumGramsPerDay,
      maximumGramsPerDay: maximumGramsPerDay,
      gramsPerKgBodyWeight: gramsPerKgBodyWeight,
      estimated30DayConsumptionGrams: estimated30DayConsumptionGrams,
      fallbackReason: null
    };
  }

  function buildFeedingPlan(input, config) {
    const validation = validateFeedingEngineConfig(config || {});
    const feedingEngine = getFeedingEngine(config || {});

    if (!validation.isValid) {
      return createFallbackPlan(input, feedingEngine, 'invalid-feeding-engine-configuration');
    }

    if (!input || !input.recipeId || !input.lifecycleStage) {
      return createFallbackPlan(input, feedingEngine, 'missing-feeding-inputs');
    }

    if (!feedingEngine || !Array.isArray(feedingEngine.guides) || !feedingEngine.guides.length) {
      return createFallbackPlan(input, feedingEngine, 'feeding-data-not-available');
    }

    const mapping = findProductMapping(feedingEngine.productMappings || [], input.recipeId, input.lifecycleStage);
    if (!mapping) {
      return createFallbackPlan(input, feedingEngine, 'unknown-product-reference');
    }

    let guide = null;
    if (input.lifecycleStage === 'adult') {
      if (typeof input.weightKg !== 'number') {
        return createFallbackPlan(input, feedingEngine, 'missing-feeding-inputs');
      }

      guide = findGuideById(feedingEngine.guides, mapping.guideId) || feedingEngine.guides.find(function (candidate) {
        return candidate && candidate.lifecycleStage === 'adult';
      }) || null;
    } else if (input.lifecycleStage === 'puppy') {
      guide = findGuideById(feedingEngine.guides, mapping.guideId);

      if (!guide && input.expectedAdultSizeClass) {
        guide = findGuideBySizeClass(feedingEngine.guides, 'puppy', input.expectedAdultSizeClass);
      }
    }

    if (!guide) {
      return createFallbackPlan(input, feedingEngine, 'missing-approved-guide');
    }

    let resolvedValue = null;
    if (guide.guideType === 'age-based-range' || guide.guideType === 'puppy-age-based-range') {
      if (typeof input.expectedAdultWeightKg !== 'number' || typeof input.puppyAgeMonths !== 'number') {
        return createFallbackPlan(input, feedingEngine, 'missing-feeding-inputs');
      }

      resolvedValue = resolveAgeBasedRangeGuide(guide, input.expectedAdultWeightKg, input.puppyAgeMonths);
    } else if (guide.guideType === 'point') {
      if (typeof input.weightKg !== 'number') {
        return createFallbackPlan(input, feedingEngine, 'missing-feeding-inputs');
      }

      resolvedValue = resolvePointGuide(guide, input.weightKg);
    } else if (guide.guideType === 'band') {
      if (typeof input.weightKg !== 'number') {
        return createFallbackPlan(input, feedingEngine, 'missing-feeding-inputs');
      }

      resolvedValue = resolveBandGuide(guide, input.weightKg);
    }

    if (!resolvedValue) {
      return createFallbackPlan(input, feedingEngine, 'exact-personalised-quantity-unavailable');
    }

    return buildCalculatedPlan(input, guide, resolvedValue);
  }

  const api = {
    createDefaultFeedingEngineConfig,
    validateFeedingEngineConfig,
    createPlaceholderFeedingPlan,
    buildFeedingPlan
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  global.FeedingEngine = api;
})(typeof globalThis !== 'undefined' ? globalThis : this);
